/**
 * State-Specific Diagnostic Questions Index
 * Exports questions for all 50 states + DC with state-specific compliance requirements
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

// Import all state question banks
import { ALABAMA_QUESTIONS } from './alabama';
import { ALASKA_QUESTIONS } from './alaska';
import { ARIZONA_QUESTIONS } from './arizona';
import { ARKANSAS_QUESTIONS } from './arkansas';
import { CALIFORNIA_QUESTIONS } from './california';
import { COLORADO_QUESTIONS } from './colorado';
import { CONNECTICUT_QUESTIONS } from './connecticut';
import { DC_QUESTIONS } from './dc';
import { DELAWARE_QUESTIONS } from './delaware';
import { FLORIDA_QUESTIONS } from './florida';
import { GEORGIA_QUESTIONS } from './georgia';
import { HAWAII_QUESTIONS } from './hawaii';
import { IDAHO_QUESTIONS } from './idaho';
import { ILLINOIS_QUESTIONS } from './illinois';
import { INDIANA_QUESTIONS } from './indiana';
import { IOWA_QUESTIONS } from './iowa';
import { KANSAS_QUESTIONS } from './kansas';
import { KENTUCKY_QUESTIONS } from './kentucky';
import { LOUISIANA_QUESTIONS } from './louisiana';
import { MAINE_QUESTIONS } from './maine';
import { MARYLAND_QUESTIONS } from './maryland';
import { MASSACHUSETTS_QUESTIONS } from './massachusetts';
import { MICHIGAN_QUESTIONS } from './michigan';
import { MINNESOTA_QUESTIONS } from './minnesota';
import { MISSISSIPPI_QUESTIONS } from './mississippi';
import { MISSOURI_QUESTIONS } from './missouri';
import { MONTANA_QUESTIONS } from './montana';
import { NEBRASKA_QUESTIONS } from './nebraska';
import { NEVADA_QUESTIONS } from './nevada';
import { NEWHAMPSHIRE_QUESTIONS } from './newhampshire';
import { NEW_JERSEY_QUESTIONS } from './newjersey';
import { NEWMEXICO_QUESTIONS } from './newmexico';
import { NEWYORK_QUESTIONS } from './newyork';
import { NORTHCAROLINA_QUESTIONS } from './northcarolina';
import { NORTH_DAKOTA_QUESTIONS } from './northdakota';
import { OHIO_QUESTIONS } from './ohio';
import { OKLAHOMA_QUESTIONS } from './oklahoma';
import { OREGON_QUESTIONS } from './oregon';
import { PENNSYLVANIA_QUESTIONS } from './pennsylvania';
import { RHODEISLAND_QUESTIONS } from './rhodeisland';
import { SOUTH_CAROLINA_QUESTIONS } from './southcarolina';
import { SOUTH_DAKOTA_QUESTIONS } from './southdakota';
import { TENNESSEE_QUESTIONS } from './tennessee';
import { TEXAS_QUESTIONS } from './texas';
import { UTAH_QUESTIONS } from './utah';
import { VERMONT_QUESTIONS } from './vermont';
import { VIRGINIA_QUESTIONS } from './virginia';
import { WASHINGTON_QUESTIONS } from './washington';
import { WESTVIRGINIA_QUESTIONS } from './westvirginia';
import { WISCONSIN_QUESTIONS } from './wisconsin';
import { WYOMING_QUESTIONS } from './wyoming';

// Map of state codes to their specific question sets
const STATE_QUESTION_BANKS: Record<string, DiagnosticQuestionConfig[]> = {
  AL: ALABAMA_QUESTIONS,
  AK: ALASKA_QUESTIONS,
  AZ: ARIZONA_QUESTIONS,
  AR: ARKANSAS_QUESTIONS,
  CA: CALIFORNIA_QUESTIONS,
  CO: COLORADO_QUESTIONS,
  CT: CONNECTICUT_QUESTIONS,
  DC: DC_QUESTIONS,
  DE: DELAWARE_QUESTIONS,
  FL: FLORIDA_QUESTIONS,
  GA: GEORGIA_QUESTIONS,
  HI: HAWAII_QUESTIONS,
  ID: IDAHO_QUESTIONS,
  IL: ILLINOIS_QUESTIONS,
  IN: INDIANA_QUESTIONS,
  IA: IOWA_QUESTIONS,
  KS: KANSAS_QUESTIONS,
  KY: KENTUCKY_QUESTIONS,
  LA: LOUISIANA_QUESTIONS,
  ME: MAINE_QUESTIONS,
  MD: MARYLAND_QUESTIONS,
  MA: MASSACHUSETTS_QUESTIONS,
  MI: MICHIGAN_QUESTIONS,
  MN: MINNESOTA_QUESTIONS,
  MS: MISSISSIPPI_QUESTIONS,
  MO: MISSOURI_QUESTIONS,
  MT: MONTANA_QUESTIONS,
  NE: NEBRASKA_QUESTIONS,
  NV: NEVADA_QUESTIONS,
  NH: NEWHAMPSHIRE_QUESTIONS,
  NJ: NEW_JERSEY_QUESTIONS,
  NM: NEWMEXICO_QUESTIONS,
  NY: NEWYORK_QUESTIONS,
  NC: NORTHCAROLINA_QUESTIONS,
  ND: NORTH_DAKOTA_QUESTIONS,
  OH: OHIO_QUESTIONS,
  OK: OKLAHOMA_QUESTIONS,
  OR: OREGON_QUESTIONS,
  PA: PENNSYLVANIA_QUESTIONS,
  RI: RHODEISLAND_QUESTIONS,
  SC: SOUTH_CAROLINA_QUESTIONS,
  SD: SOUTH_DAKOTA_QUESTIONS,
  TN: TENNESSEE_QUESTIONS,
  TX: TEXAS_QUESTIONS,
  UT: UTAH_QUESTIONS,
  VT: VERMONT_QUESTIONS,
  VA: VIRGINIA_QUESTIONS,
  WA: WASHINGTON_QUESTIONS,
  WV: WESTVIRGINIA_QUESTIONS,
  WI: WISCONSIN_QUESTIONS,
  WY: WYOMING_QUESTIONS,
};

/**
 * Get state-specific diagnostic questions
 * Returns the complete question set for a state
 */
export function getStateSpecificQuestions(stateCode: string): DiagnosticQuestionConfig[] | null {
  const questions = STATE_QUESTION_BANKS[stateCode.toUpperCase()];
  return questions || null;
}

/**
 * Check if a state has specific diagnostic questions
 */
export function hasStateSpecificQuestions(stateCode: string): boolean {
  return stateCode.toUpperCase() in STATE_QUESTION_BANKS;
}

/**
 * Get all states that have specific question banks
 */
export function getStatesWithQuestionBanks(): string[] {
  return Object.keys(STATE_QUESTION_BANKS);
}

export {
  ALABAMA_QUESTIONS,
  ALASKA_QUESTIONS,
  ARIZONA_QUESTIONS,
  ARKANSAS_QUESTIONS,
  CALIFORNIA_QUESTIONS,
  COLORADO_QUESTIONS,
  CONNECTICUT_QUESTIONS,
  DC_QUESTIONS,
  DELAWARE_QUESTIONS,
  FLORIDA_QUESTIONS,
  GEORGIA_QUESTIONS,
  HAWAII_QUESTIONS,
  IDAHO_QUESTIONS,
  ILLINOIS_QUESTIONS,
  INDIANA_QUESTIONS,
  IOWA_QUESTIONS,
  KANSAS_QUESTIONS,
  KENTUCKY_QUESTIONS,
  LOUISIANA_QUESTIONS,
  MAINE_QUESTIONS,
  MARYLAND_QUESTIONS,
  MASSACHUSETTS_QUESTIONS,
  MICHIGAN_QUESTIONS,
  MINNESOTA_QUESTIONS,
  MISSISSIPPI_QUESTIONS,
  MISSOURI_QUESTIONS,
  MONTANA_QUESTIONS,
  NEBRASKA_QUESTIONS,
  NEVADA_QUESTIONS,
  NEWHAMPSHIRE_QUESTIONS,
  NEW_JERSEY_QUESTIONS,
  NEWMEXICO_QUESTIONS,
  NEWYORK_QUESTIONS,
  NORTHCAROLINA_QUESTIONS,
  NORTH_DAKOTA_QUESTIONS,
  OHIO_QUESTIONS,
  OKLAHOMA_QUESTIONS,
  OREGON_QUESTIONS,
  PENNSYLVANIA_QUESTIONS,
  RHODEISLAND_QUESTIONS,
  SOUTH_CAROLINA_QUESTIONS,
  SOUTH_DAKOTA_QUESTIONS,
  TENNESSEE_QUESTIONS,
  TEXAS_QUESTIONS,
  UTAH_QUESTIONS,
  VERMONT_QUESTIONS,
  VIRGINIA_QUESTIONS,
  WASHINGTON_QUESTIONS,
  WESTVIRGINIA_QUESTIONS,
  WISCONSIN_QUESTIONS,
  WYOMING_QUESTIONS,
};
